import { ttsAssetKey } from "./ttsAssetKey";

// 事前生成音声の照会 (spec: docs/superpowers/specs/2026-07-26-prerendered-tts-design.md §4.3)。
// manifest は pnpm tts:build が public/tts/manifest.json に出力する。
export type TtsManifest = {
  voiceId: string;
  model: string;
  entries: { key: string; text: string; chars: number }[];
};

const MANIFEST_URL = "/tts/manifest.json";

// 1 セッションで 1 回だけ取得する。失敗しても null を焼き付けて再試行しない
// (毎回の読み上げで 404 を叩き続けないため)。
let manifestPromise: Promise<TtsManifest | null> | null = null;

function isManifest(v: unknown): v is TtsManifest {
  if (typeof v !== "object" || v === null) return false;
  const m = v as Record<string, unknown>;
  return typeof m.voiceId === "string" && typeof m.model === "string" && Array.isArray(m.entries);
}

function loadManifest(): Promise<TtsManifest | null> {
  if (manifestPromise) return manifestPromise;
  manifestPromise = (async () => {
    try {
      const res = await fetch(MANIFEST_URL);
      if (!res.ok) return null;
      const json: unknown = await res.json();
      return isManifest(json) ? json : null;
    } catch {
      return null;
    }
  })();
  return manifestPromise;
}

/**
 * 事前生成済みの音声があれば Blob を返す。無ければ null。
 *
 * 失敗経路 (manifest 無し / キー不一致 / mp3 取得失敗 / 例外) はすべて null を返し、
 * 呼び出し側を API 合成へ倒す。ここで throw すると読み上げが止まる (原則 5)。
 */
export async function lookupPrerendered(text: string): Promise<Blob | null> {
  try {
    const manifest = await loadManifest();
    if (!manifest) return null;
    const key = await ttsAssetKey(text, manifest.voiceId, manifest.model);
    if (!manifest.entries.some((e) => e.key === key)) return null;
    const res = await fetch(`/tts/${key}.mp3`);
    if (!res.ok) return null;
    // Cloudflare Pages は存在しないパスに 404 ではなく SPA fallback の index.html を
    // HTTP 200 で返す (scripts/verify-prod.sh に実測記録あり)。content-type で弾かないと
    // HTML を音声として再生してしまい、API 合成への fallback も効かなくなる (原則 5)。
    if (!(res.headers.get("content-type") ?? "").includes("audio")) return null;
    return new Blob([await res.arrayBuffer()], { type: "audio/mpeg" });
  } catch {
    return null;
  }
}
